import { TextField } from "@kobalte/core/text-field";
import { getIconComponent } from "@storytell/bsky-icons";
import { CreateViewRequestConfig, UpdateViewRequestConfig, useStorytellClient } from "@storytell/client";
import type { Asset, AssetId, Concept, ConceptId, FilterOperator, Label, LabelId, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbArrowRight, TbFilter, TbFilterPlus, TbX } from "solid-icons/tb";
import { For, Match, type Setter, Show, Switch, batch, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useWire } from "~/wire";
import { TbStackPlus } from "../knowledge/TbStackPlus";
import { LabelPicker } from "../labels";
import { isLightColor } from "../layouts/minimal/isLightColor";
import { type FilterBuilder, type UIFilterGroup, isPositiveOperator, uiFiltersToServerFilters } from "./filterBuilder";

export const Filters = (props: {
  builder: FilterBuilder<Asset | Concept>;
  hasViewChanges: boolean;
  editView: boolean;
  setEditView: Setter<boolean>;
  activeView: ViewId | "all" | "new";
}) => {
  const wire = useWire();
  const [search, setSearch] = createSignal("");
  const filterLabelsBySearch = () => wire.sync.labels.search(search());

  let lastView = null as ViewId | "all" | "new" | null;
  createEffect((last) => {
    const view = props.activeView;
    lastView = (last ?? null) as ViewId | "all" | "new" | null;
    return view;
  });

  const viewData = () => {
    if (props.activeView === "new" || props.activeView === "all") return null;
    return wire.sync.views.get(props.activeView);
  };

  const addLabelFilter = (labelId: string, groupIndex: number) => {
    props.builder.addFilter(
      {
        filter: (entity) =>
          entity.id.startsWith("asset_")
            ? wire.sync.getAssetHasLabel(entity.id as AssetId, labelId as LabelId)
            : wire.sync.getConceptHasLabel(entity.id as ConceptId, labelId as LabelId),
        id: labelId as LabelId,
        kind: "label",
      },
      groupIndex,
    );
  };

  const removeLabelFilter = (labelId: string, groupIndex: number) => {
    props.builder.removeFilter(labelId, groupIndex);
  };

  const operator = () => props.builder.state.filter.operator;

  const activeOption = () =>
    props.builder.state.filter.groups.length <= 1
      ? undefined
      : operator() === "and"
        ? "match all filters"
        : "match any filter";

  const [open, setOpen] = createSignal(false);

  const [tempData, setTempData] = createSignal({
    selectedLabels: [] as LabelId[],
  });

  const updateView = async (viewId: ViewId, name?: string) => {
    const client = useStorytellClient();
    const request = UpdateViewRequestConfig.withBearerToken(wire.auth.token());
    const response = await client.fetch(
      request,
      { id: viewId },
      {
        id: viewId,
        name: name || wire.sync.views.get(viewId)?.name || "",
        description: "",
        filters: uiFiltersToServerFilters(props.builder.state.filter),
      },
    );

    if (response.isOk()) {
      wire.sync.views.set(response.value.id, { ...response.value, userId: wire.auth.user()?.id || "" });
      props.setEditView(false);
    }
  };

  const createView = async (name: string) => {
    const client = useStorytellClient();
    const request = CreateViewRequestConfig.withBearerToken(wire.auth.token());

    const response = await client.fetch(request, undefined, {
      name: name,
      description: "",
      filters: uiFiltersToServerFilters(props.builder.state.filter),
    });

    if (response.isOk()) {
      wire.sync.views.set(response.value.id, { ...response.value, userId: wire.auth.user()?.id || "" });
      wire.knowledgeUi.setActiveView(response.value.id);
    }
  };

  const formId = "create-new-view-form";
  const onSubmit = async () => {
    const form = document.getElementById(formId) as HTMLFormElement;
    const data = new FormData(form);
    const name = data.get("viewName")?.toString();
    if (!name) return;
    if (props.editView && props.activeView !== "new" && props.activeView !== "all") {
      await updateView(props.activeView, name);
      return;
    }
    await createView(name);
  };

  return (
    <div class="flex flex-col gap-2">
      <Show when={props.activeView === "new" || (props.editView && props.activeView !== "all")}>
        {(_) => {
          onMount(() => {
            const input = document.getElementById("view-name") as HTMLInputElement;
            input.focus();
          });
          return (
            <form
              id={formId}
              class="w-sm flex flex-col"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              <div class="flex gap-2">
                <label for="view-name" class="mt-1 text-sm text-secondary-text">
                  View Name
                </label>
                <TextField required>
                  <TextField.Input
                    value={viewData()?.name}
                    id="view-name"
                    name="viewName"
                    class="w-full px-3 py-1 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder=""
                  />
                </TextField>
              </div>
            </form>
          );
        }}
      </Show>

      <div class="flex items-center gap-2 justify-between">
        <div class="flex items-center gap-2 flex-wrap">
          <Show when={activeOption()}>
            {(option) => (
              <button
                type="button"
                class="text-xs border flex items-center gap-1 shrink-0 border-primary-border rounded-md px-1.5 py-1 hover:bg-panel-hover cursor-pointer"
                onClick={() => {
                  props.builder.changeGlobalOperator(operator() === "and" ? "or" : "and");
                }}
              >
                {option()}
                <TbArrowRight class="stroke-2" />
              </button>
            )}
          </Show>
          <For each={props.builder.state.filter.groups}>
            {(group, groupIndex) => (
              <FilterGroup
                onRemove={() => props.builder.removeFilterGroup(groupIndex())}
                onSelectOperator={(op) => {
                  props.builder.changeGroupOperator(op, groupIndex());
                }}
                onSelectLabel={(l) => addLabelFilter(l.id, groupIndex())}
                onDeselectLabel={(l) => removeLabelFilter(l.id, groupIndex())}
                group={group}
                groupIndex={groupIndex()}
              />
            )}
          </For>

          <StPopover
            contentProps={{ class: "z-50" }}
            placement="bottom-start"
            noArrow
            gutter={10}
            open={open()}
            onOpenChange={(open) => {
              setOpen(open);
              setTempData({ selectedLabels: [] });
              requestAnimationFrame(() => {
                if (!open) {
                  if (props.builder.state.filter.groups.at(-1)?.filters.length === 0) {
                    props.builder.removeFilterGroup(props.builder.state.filter.groups.length - 1);
                  }
                }
              });
            }}
            content={
              <LabelPicker
                disableCreate
                search={search()}
                setSearch={setSearch}
                labels={filterLabelsBySearch()}
                selectedLabels={tempData().selectedLabels}
                onSelectLabel={(l) => {
                  batch(() => {
                    if (tempData().selectedLabels.length === 0) {
                      props.builder.addFilterGroup("and", []);
                    }
                    setTempData((d) => ({ ...d, selectedLabels: [...d.selectedLabels, l.id] }));
                    addLabelFilter(l.id, props.builder.state.filter.groups.length - 1);
                  });
                }}
                onDeselectLabel={(l) => {
                  batch(() => {
                    setTempData((d) => ({ ...d, selectedLabels: d.selectedLabels.filter((id) => id !== l.id) }));
                    removeLabelFilter(l.id, props.builder.state.filter.groups.length - 1);
                    if (tempData().selectedLabels.length === 0) {
                      props.builder.removeFilterGroup(props.builder.state.filter.groups.length - 1);
                    }
                  });
                }}
                onCreateLabel={async () => {}}
              />
            }
          >
            <StPopoverTrigger
              as={StButton}
              icon={TbFilterPlus}
              noBorder
              iconClass="size-3.5 stroke-[2.5px]"
              aria-label="Add Filter"
            />
          </StPopover>
        </div>

        <div class="flex items-center gap-2">
          <div class="w-px h-3 bg-primary-border" />
          <Show when={props.builder.state.filter.groups.length > 0 && props.activeView !== "new"}>
            <StButton noBorder noColor class="py-1" onClick={props.builder.resetFilter}>
              Clear
            </StButton>
          </Show>

          <Switch>
            <Match when={props.activeView === "new"}>
              <StButton
                noBorder
                noColor
                class="py-1"
                onClick={() => {
                  console.log("cancel", lastView);
                  if (lastView) wire.knowledgeUi.setActiveView(lastView);
                  else wire.knowledgeUi.setActiveView("all");
                }}
              >
                Cancel
              </StButton>
              <StButton class="py-1" icon={TbStackPlus} type="submit" form={formId}>
                Create View
              </StButton>
            </Match>

            <Match when={props.hasViewChanges && props.activeView === "all"}>
              <StButton
                class="py-1"
                icon={TbStackPlus}
                onClick={() => {
                  wire.knowledgeUi.setActiveView("new");
                }}
              >
                Save as View
              </StButton>
            </Match>
            <Match when={props.editView}>
              <StButton
                noBorder
                noColor
                class="py-1"
                onClick={() => {
                  props.setEditView(false);
                }}
              >
                Cancel
              </StButton>
              <StButton class="py-1" icon={TbStackPlus} type="submit" form={formId}>
                Save Changes
              </StButton>
            </Match>
            <Match when={props.hasViewChanges}>
              <StDropdown
                gutter={10}
                placement="bottom-end"
                contentProps={{ class: "z-50" }}
                content={
                  <div>
                    <StDropdownItem
                      onClick={() => updateView(props.activeView as ViewId, viewData()?.name)}
                      class="px-2 py-1 hover:bg-tile-hover cursor-pointer rounded-md"
                    >
                      Save to {viewData()?.name}
                    </StDropdownItem>
                    <StDropdownItem
                      class="px-2 py-1 hover:bg-tile-hover cursor-pointer rounded-md"
                      onClick={() => {
                        wire.knowledgeUi.setActiveView("new");
                      }}
                    >
                      Save as new View
                    </StDropdownItem>
                  </div>
                }
              >
                <StDropdownTrigger as={StButton} class="py-1" icon={TbStackPlus}>
                  Save
                </StDropdownTrigger>
              </StDropdown>
            </Match>
          </Switch>
        </div>
      </div>
    </div>
  );
};

export const FilterGroup = (props: {
  group: UIFilterGroup<Asset | Concept>;
  groupIndex: number;
  onSelectLabel: (label: Label) => void;
  onDeselectLabel: (label: Label) => void;
  onSelectOperator: (operator: FilterOperator) => void;
  onRemove: () => void;
}) => {
  const [search, setSearch] = createSignal("");
  const filterLabelsBySearch = () => wire.sync.labels.search(search());
  const wire = useWire();
  const operator = () => props.group.operator;
  const filters = () => props.group.filters;

  const activeOption = () => {
    const o = operator();
    if (filters().length <= 1) {
      if (isPositiveOperator(o)) {
        return "include";
      }
      return "exclude";
    }

    if (o === "and") {
      return "include all of";
    }
    if (o === "or") {
      return "include any of";
    }
    if (o === "nand") {
      return "exclude if all";
    }

    return "exclude if any of";
  };

  const options = () => {
    if (filters().length <= 1) {
      return [
        { label: "include", value: "and" },
        { label: "exclude", value: "nand" },
      ] as const;
    }
    return [
      { label: "include all of", value: "and" },
      { label: "include any of", value: "or" },
      { label: "exclude if all", value: "nand" },
      { label: "exclude if any of", value: "nor" },
    ] as const;
  };

  return (
    <Show when={filters().length > 0}>
      <div class="rounded-md border border-primary-border bg-panel flex items-center shrink-0">
        <StDropdown
          gutter={10}
          contentProps={{ class: "z-50" }}
          content={
            <div>
              <For each={options()}>
                {(opt) => (
                  <StDropdownItem
                    class="px-2 py-1 hover:bg-tile-hover cursor-pointer rounded-md"
                    onClick={() => props.onSelectOperator(opt.value)}
                  >
                    {opt.label}
                  </StDropdownItem>
                )}
              </For>
            </div>
          }
        >
          <StDropdownTrigger
            onClick={() => props.onSelectOperator(operator() === "and" ? "or" : "and")}
            type="button"
            class="px-2 text-xs hover:bg-panel-hover h-6 rounded-l-md cursor-pointer"
          >
            {activeOption()}
          </StDropdownTrigger>
        </StDropdown>
        <div class="w-px h-6 bg-primary-border" />

        <StPopover
          contentProps={{ class: "z-50" }}
          placement="bottom-start"
          noArrow
          gutter={10}
          content={
            <LabelPicker
              disableCreate
              search={search()}
              setSearch={setSearch}
              labels={filterLabelsBySearch()}
              selectedLabels={props.group.filters.map((f) => f.id as LabelId) ?? []}
              onSelectLabel={props.onSelectLabel}
              onDeselectLabel={props.onDeselectLabel}
              onCreateLabel={async () => {}}
            />
          }
        >
          <StPopoverTrigger
            as="button"
            type="button"
            class="group/btn px-1 text-xs flex items-center gap-1 relative hover:bg-panel-hover h-6 cursor-pointer"
          >
            <For
              each={
                filters()
                  .filter((f) => f.kind === "label")
                  .map((f) => wire.sync.labels.get(f.id as LabelId))
                  .filter(Boolean)
                  .slice(0, 4) as Label[]
              }
            >
              {(label, index) => (
                <div
                  class="size-4 rounded-full grid place-content-center absolute border border-muted-border group-hover/btn:scale-hover group-active/btn:scale-active transition-transform"
                  style={{
                    "background-color": label.color,
                    color: isLightColor(label.color) ? "black" : "white",
                    left: `${index() * 9 + 4}px`,
                  }}
                >
                  <Dynamic component={getIconComponent(label.icon)} class="size-3" />
                </div>
              )}
            </For>
            <span class="text-xs max-w-20 truncate" style={{ "margin-left": `${filters().length * 9 + 12}px` }}>
              {filters().length === 1
                ? wire.sync.labels.get(filters()[0]?.id as LabelId)?.name
                : `${filters().length} labels`}
            </span>
          </StPopoverTrigger>
        </StPopover>
        <div class="w-px h-6 bg-primary-border" />
        <button
          type="button"
          aria-label="Remove filter"
          onClick={props.onRemove}
          class="group/btn px-1.5 text-xs hover:bg-panel-hover h-6 cursor-pointer rounded-r-md"
        >
          <TbX class="stroke-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
        </button>
      </div>
    </Show>
  );
};
